/**
 * Tandem — consultant visit points model (business spec 5.0).
 *
 * Classify a visit from its name, award BASE points from a first-match-wins
 * waterfall, then group visits at the same account on the same day into an
 * OCCASION whose base is the MAX of its visits plus a supplement per extra
 * assessment. That total is RECOGNITION (what gets reported); the
 * un-supplemented base is CAPACITY (what decides whether two occasions share a
 * day). Pure module — no React, no API — so it can be tested in isolation.
 *
 * Divergences from the reference implementation, and why the score can be
 * flagged rather than wrong, are in the PR description and Tandem-Spec.md.
 */

export type VisitType =
    | "FACT_FIND"
    | "GRA_FRA"
    | "FRA"
    | "GRA"
    | "WPI"
    | "CONSULTANCY_HALF"
    | "CONSULTANCY_FULL"
    | "CONSULTANCY_UNSPEC"
    | "GENERIC";

export const VISIT_TYPE_LABEL: Record<VisitType, string> = {
    FACT_FIND: "Fact find",
    GRA_FRA: "GRA + FRA",
    FRA: "Fire risk assessment",
    GRA: "General risk assessment",
    WPI: "Workplace inspection",
    CONSULTANCY_HALF: "Consultancy (half day)",
    CONSULTANCY_FULL: "Consultancy (full day)",
    CONSULTANCY_UNSPEC: "Consultancy (unspecified)",
    GENERIC: "Other",
};

/** The 14 industry rates. Keys are matched EXACTLY — all 14 exist verbatim in
 *  Salesforce's Account.Industry picklist (verified against production). */
export const DEFAULT_INDUSTRY_RATES: Record<string, number> = {
    // Half a day
    Societies: 1,
    Logistics: 1,
    "Professional & Skilled Services": 1,
    Charity: 1,
    Property: 1,
    "Retail & Wholesale": 1,
    Health: 1,
    Hospitality: 1,
    // A full day
    "Construction & Trades": 2,
    Care: 2,
    "Public & Government": 2,
    "Manufacture & Repair": 2,
    "Agriculture & Energy": 2,
    "Education & Training": 2,
};

export interface PointsConfig {
    industryRates: Record<string, number>;
    remotePoints: number;
    wpiPoints: number;
    consultancyFullPoints: number;
    consultancyHalfPoints: number;
    /** Unspecified consultancy is assumed a full day by the spec. No fixture pins it. */
    consultancyUnspecPoints: number;
    /** The high-risk fire escalation, applied to fire assessments at a site
     *  whose FRA_Risk_Level__c is "High Risk". */
    highRiskFraPoints: number;
    /** What to award when the account has no industry, or one outside the 14.
     *  The business has settled this at 2: if the fact find has not happened the
     *  industry gets confirmed there, and if it has, the visit is awarded 2 and
     *  the consultant updates Salesforce afterwards. So the points are paid out —
     *  but the visit is still flagged via `industryMissing` so those accounts can
     *  be listed for the consultant to fix. Set to `null` to withhold points
     *  instead. */
    scoreUnknownIndustryAs: number | null;
    supplementPerExtraAssessment: number;
    /** Settled by the business: the supplement is per REPORT produced, not per
     *  Salesforce record. A combined GRA+FRA is one record but two reports, so it
     *  scores base + one supplement.
     *
     *  The reasoning matters, because it is what justifies the recognition/capacity
     *  split: visit-time data shows a GRA+FRA takes no longer on site than a GRA
     *  alone, so the DEMAND is one point. The extra 0.5 recognises the second
     *  report, and is deliberately excluded from capacity — a 1.5 occasion still
     *  consumes only half a day's demand, which is what keeps it pairable.
     *
     *  "per-record" is retained because it is what the reference implementation
     *  does, so the two can be compared if a figure is ever disputed. */
    supplementBasis: "per-record" | "per-assessment";
    remoteBatchSize: number;
    /**
     * Field days per consultant per year: 180, i.e. 15 a month, as confirmed by
     * the business.
     *
     * This is what makes the model hang together. A full day is 2 points, so 15
     * field days is 30 available points — exactly the monthly target. Hitting the
     * target and being fully utilised are therefore the same thing, which is why
     * utilisation is reported in points rather than in days.
     *
     * Recorded for whoever raises it: measured delivery over three complete years
     * is a median of 126 field days a year, about 10.5 a month (126.0 / 126.5 /
     * 126.0 for 2023 / 2024 / 2025), counting calendar days with at least one
     * non-Remote visit across consultants active 11+ months. That is well short of
     * 15 a month, so either the basis is aspirational or delivery is being
     * under-recorded. A question for the business, not a licence to change this.
     *
     * Two traps if anyone re-measures. Do NOT gate on visitCategory == "On Site":
     * the blank rate fell from 26% to 9% over the period, so that yields
     * 89/97/119 days and reads as a 33% productivity rise that did not happen.
     * And Visit__c holds the FORWARD diary, so a live measure must filter to
     * delivered visits — the unfiltered current year overstates by 27%.
     */
    fieldDaysPerYear: number;
    monthlyPointsTarget: number;
    /** Occasions with capacity exactly this value can be paired onto one day. */
    pairableCapacity: number;
    pairingMaxGapDays: number;
}

export const DEFAULT_POINTS_CONFIG: PointsConfig = {
    industryRates: DEFAULT_INDUSTRY_RATES,
    remotePoints: 0.5,
    wpiPoints: 1,
    consultancyFullPoints: 2,
    consultancyHalfPoints: 1,
    consultancyUnspecPoints: 2,
    highRiskFraPoints: 2,
    scoreUnknownIndustryAs: 2,
    supplementPerExtraAssessment: 0.5,
    supplementBasis: "per-assessment",
    remoteBatchSize: 4,
    fieldDaysPerYear: 180,
    monthlyPointsTarget: 30,
    pairableCapacity: 1,
    pairingMaxGapDays: 90,
};

// Step 1: classification. First match wins and the order is load-bearing — FRA
// before GRA, and "half" before "full" so "Full or Half Day" resolves to half.

const word = (w: string, s: string) => new RegExp(`\\b${w}\\b`).test(s);

export const classify = (visitName?: string | null): VisitType => {
    const s = (visitName || "").toLowerCase();

    if (s.includes("fact find")) return "FACT_FIND";

    const gra = word("gra", s) || s.includes("general risk");
    const fra = word("fra", s) || s.includes("fire risk");
    if (gra && fra) return "GRA_FRA";
    if (fra) return "FRA";
    if (gra) return "GRA";

    if (s.includes("workplace inspection") || word("wpi", s)) return "WPI";

    if (s.includes("consultancy")) {
        if (s.includes("half")) return "CONSULTANCY_HALF";
        if (s.includes("full")) return "CONSULTANCY_FULL";
        return "CONSULTANCY_UNSPEC";
    }

    return "GENERIC";
};

// ── Step 2: base points ──────────────────────────────────────────────────────

export type UnratedReason = "fact-find" | "no-industry" | "off-taxonomy" | "unknown-mode";

export const UNRATED_LABEL: Record<UnratedReason, string> = {
    "fact-find": "Fact finds are excluded from the points model",
    "no-industry": "No industry set on the account, so no rate applies",
    "off-taxonomy": "Account industry isn't one of the 14 rated industries",
    "unknown-mode": "Visit is neither On Site nor Remote, so it can't be scored",
};

export interface ScorableVisit {
    subActivity?: string | null;
    /** The visit's own name. Read ONLY to spot fact finds — see scoreVisit. */
    name?: string | null;
    visitCategory?: string | null; // "On Site" | "Remote" | null
    industry?: string | null;
    /** Site fire-risk band, from Account.FRA_Risk_Level__c: "High Risk" or
     *  "Low Risk". Compared exactly — a value of "high risk" or "High" loses the
     *  escalation and silently halves a high-risk fire assessment.
     *
     *  Careful: this field DEFAULTS to "Low Risk" in Salesforce, so "Low Risk"
     *  does not mean the site was assessed and found low risk. It may mean nobody
     *  has looked. That is why highRiskFra is read as well. */
    fraRiskLevel?: string | null;

    /** Account.High_Risk_FRA__c — the older checkbox. Needs a deliberate tick, so
     *  where it is set it is a positive signal even if the picklist sits at its
     *  default. Never the sole input: it is falling out of use (ticked on 1 of 239
     *  high-risk accounts created this year, against ~90% in 2018), so on its own
     *  it would miss most high-risk sites. */
    highRiskFra?: boolean | null;
}

export interface VisitScore {
    type: VisitType;
    /** Base points, or null when the visit cannot be honestly scored. */
    points: number | null;
    /** Which waterfall rule decided it — shown in the explainer. */
    rule: string;
    unrated?: UnratedReason;
    /** A fire assessment whose site risk band is unknown. Rule 4 would escalate
     *  it to a full day at a High Risk site, and no field feeds that today, so
     *  the figure shown may be too low. */
    mayBeUnderScored?: boolean;
    /** The account has no industry, or one outside the 14. Points may still have
     *  been awarded (see `scoreUnknownIndustryAs`) — this flags the data gap so
     *  the account can be chased, not that the score is missing. */
    industryMissing?: boolean;
    /** Scored as on-site because the visit has no On Site / Remote flag yet.
     *  Only set when the caller asked for that assumption — see scoreVisit. */
    modeAssumed?: boolean;
}

export const scoreVisit = (
    v: ScorableVisit,
    cfg: PointsConfig = DEFAULT_POINTS_CONFIG,
    opts?: {
        /**
         * Score a visit with no On Site / Remote flag as if it were on site.
         *
         * For UNBOOKED work only. That flag is set when a visit is arranged, so
         * 85% of the pool has none — and refusing to score it leaves every row
         * blank, which tells the planner nothing. On-site is the right
         * assumption: 82% of booked visits are on site against 9% remote, and
         * the work in question is site work.
         *
         * Never pass this for delivered visits. There, an unset mode is a real
         * gap and should stay visible rather than being assumed away.
         */
        assumeOnSiteIfUnset?: boolean;
    },
): VisitScore => {
    // Classification reads subActivity, except for fact finds: the picklist has no
    // value for them, so the NAME wins. Delete once Sub_Activity__c has one.
    const type = classify(v.name) === "FACT_FIND" ? "FACT_FIND" : classify(v.subActivity);

    const modeUnset = v.visitCategory == null || v.visitCategory === "";
    const assumed = !!opts?.assumeOnSiteIfUnset && modeUnset;
    const score = scoreType(type, assumed ? { ...v, visitCategory: "On Site" } : v, cfg);

    return assumed ? { ...score, modeAssumed: true } : score;
};

/** The waterfall, given an already-known visit type. Separated from
 *  {@link scoreVisit} so the scoring rules can be exercised independently of
 *  classification — which is exactly how the business fixtures are written. */
export const scoreType = (
    type: VisitType,
    v: Omit<ScorableVisit, "subActivity">,
    cfg: PointsConfig = DEFAULT_POINTS_CONFIG,
): VisitScore => {
    // Fact finds are filtered out before scoring in the spec.
    if (type === "FACT_FIND") {
        return { type, points: null, rule: "Fact find — outside the points model", unrated: "fact-find" };
    }

    // Rule 1 — Remote beats everything, including a high-risk fire assessment.
    if (v.visitCategory === "Remote") {
        return { type, points: cfg.remotePoints, rule: "Remote visit" };
    }

    // The spec has no branch for a mode that is neither value and drops such
    // visits from the day model while still scoring them. Refusing is honest.
    if (v.visitCategory !== "On Site") {
        return { type, points: null, rule: "Neither On Site nor Remote — cannot be scored", unrated: "unknown-mode" };
    }

    // Rule 2 — a workplace inspection is a flat rate in every industry.
    if (type === "WPI") return { type, points: cfg.wpiPoints, rule: "Workplace inspection" };

    // Rule 3 — consultancy days are priced by length, not industry.
    if (type === "CONSULTANCY_FULL") return { type, points: cfg.consultancyFullPoints, rule: "Consultancy, full day" };
    if (type === "CONSULTANCY_HALF") return { type, points: cfg.consultancyHalfPoints, rule: "Consultancy, half day" };
    if (type === "CONSULTANCY_UNSPEC")
        return { type, points: cfg.consultancyUnspecPoints, rule: "Consultancy, length not stated (assumed a full day)" };

    // Rule 4 — a fire assessment at a high-risk SITE is a full day; other types
    // keep their normal rate. Either field marks it high risk; they disagree on 8%.
    const fireAssessment = type === "FRA" || type === "GRA_FRA";
    const highRiskSite = v.fraRiskLevel === "High Risk" || v.highRiskFra === true;
    if (fireAssessment && highRiskSite) {
        return { type, points: cfg.highRiskFraPoints, rule: "Fire assessment at a High Risk site" };
    }

    // Flag only when NEITHER field has anything to say, so the visit fell through
    // to the industry rate without the risk band ever being considered.
    const riskUnknown =
        fireAssessment && (v.fraRiskLevel == null || v.fraRiskLevel === "") && v.highRiskFra == null;

    // Rules 5 and 6 — the industry rate.
    const industry = v.industry;
    const rated = industry != null && industry !== "" && Object.prototype.hasOwnProperty.call(cfg.industryRates, industry);

    if (!rated) {
        const reason: UnratedReason = industry == null || industry === "" ? "no-industry" : "off-taxonomy";
        if (cfg.scoreUnknownIndustryAs == null) {
            return {
                type,
                points: null,
                rule: "No industry set on the account",
                unrated: reason,
                mayBeUnderScored: riskUnknown,
                industryMissing: true,
            };
        }
        return {
            type,
            points: cfg.scoreUnknownIndustryAs,
            rule: "No industry set — awarded the default",
            mayBeUnderScored: riskUnknown,
            industryMissing: true,
        };
    }

    return {
        type,
        points: cfg.industryRates[industry as string],
        rule: `${industry} industry rate`,
        mayBeUnderScored: riskUnknown,
    };
};

// Step 3: occasions. All on-site visits at the same account on the same day
// are ONE occasion, whatever the mix of visit types.

export interface OccasionVisit extends ScorableVisit {
    id?: string;
    accountId?: string | null;
    accountName?: string | null;
    date: string; // yyyy-MM-dd
}

export interface Occasion {
    key: string;
    accountId: string;
    accountName?: string | null;
    date: string;
    visits: OccasionVisit[];
    scores: VisitScore[];
    /** MAX of the visits' base points — never the sum. Null if none could be scored. */
    base: number | null;
    supplement: number;
    /** base + supplement. The reported score. */
    recognition: number | null;
    /** base only. What the day is actually consumed by. */
    capacity: number | null;
    /** Visits in this occasion that could not be scored at all. */
    unratedCount: number;
    /** Visits scored off a missing industry — points awarded, data still to fix. */
    industryMissingCount: number;
    mayBeUnderScored: boolean;
}

/** Counts the assessments a single record represents. Only differs from 1 when
 *  the business decides a combined GRA+FRA record earns the supplement. */
const assessmentsIn = (type: VisitType, cfg: PointsConfig) =>
    cfg.supplementBasis === "per-assessment" && type === "GRA_FRA" ? 2 : 1;

/**
 * Groups a consultant's visits into occasions.
 *
 * PASS ONE CONSULTANT'S VISITS AT A TIME. Grouping is by account and day only,
 * so a mixed list would merge two different consultants who happened to visit
 * the same account on the same day into a single occasion.
 *
 * This is also what handles a combined GRA + FRA. Producing both reports takes
 * two Salesforce visit records, and two records at one account on one day are
 * one occasion — highest base plus a supplement for the second report. There is
 * nothing extra to detect: a single record cannot yield two reports, so a visit
 * whose NAME reads like a combined job is still one report's worth of work.
 */
export const buildOccasions = (
    visits: OccasionVisit[],
    cfg: PointsConfig = DEFAULT_POINTS_CONFIG,
): Occasion[] => {
    const groups = new Map<string, OccasionVisit[]>();

    for (const v of visits) {
        const score = scoreVisit(v, cfg);
        // Remote work and fact finds never enter an occasion, so they can never
        // earn a supplement.
        if (score.type === "FACT_FIND" || v.visitCategory !== "On Site") continue;

        // Group on the account ID. Account NAME is not safe — two accounts can
        // share a display name, which would silently merge their occasions.
        const account = v.accountId || v.accountName || "";
        if (!account) continue;

        const key = `${account}|${v.date}`;
        const list = groups.get(key);
        if (list) list.push(v);
        else groups.set(key, [v]);
    }

    const out: Occasion[] = [];
    for (const [key, group] of groups) {
        const scores = group.map((v) => scoreVisit(v, cfg));
        const scored = scores.map((s) => s.points).filter((p): p is number => p != null);

        const base = scored.length ? Math.max(...scored) : null;
        const assessments = group.reduce((n, v, i) => n + assessmentsIn(scores[i].type, cfg), 0);
        const supplement = Math.max(0, assessments - 1) * cfg.supplementPerExtraAssessment;

        out.push({
            key,
            accountId: group[0].accountId || group[0].accountName || "",
            accountName: group[0].accountName,
            date: group[0].date,
            visits: group,
            scores,
            base,
            supplement,
            recognition: base == null ? null : base + supplement,
            capacity: base,
            unratedCount: scores.filter((s) => s.points == null).length,
            industryMissingCount: scores.filter((s) => s.industryMissing).length,
            mayBeUnderScored: scores.some((s) => s.mayBeUnderScored),
        });
    }

    return out.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
};

/**
 * Points over any set of visits — the occasion arithmetic applied in bulk.
 *
 * Groups into occasions (same account, same day), so several reports at one site
 * score the highest base plus a supplement rather than being summed. Remote
 * visits sit outside occasions and score individually.
 *
 * Shared so a day, a week, a month and a consultant's history are all computed
 * the same way and cannot drift apart.
 */
export const periodPoints = (visits: OccasionVisit[], cfg: PointsConfig = DEFAULT_POINTS_CONFIG) => {
    const occasions = buildOccasions(visits, cfg);
    const remote = visits.filter((v) => v.visitCategory === "Remote").length * cfg.remotePoints;
    const r = (n: number) => Math.round(n * 10) / 10;
    return {
        recognition: r(occasions.reduce((n, o) => n + (o.recognition ?? 0), 0) + remote),
        capacity: r(occasions.reduce((n, o) => n + (o.capacity ?? 0), 0) + remote),
        unrated: occasions.reduce((n, o) => n + o.unratedCount, 0),
        occasions: occasions.length,
    };
};

// Step 4: days. Every occasion costs a whole day on its own; a pairable one
// halves only by actually being paired, so `pairs` is 0 until routing exists.

export const remoteDays = (remoteVisitCount: number, cfg: PointsConfig = DEFAULT_POINTS_CONFIG) =>
    Math.ceil(remoteVisitCount / cfg.remoteBatchSize);

export const daysRequired = (occasionCount: number, pairCount = 0) => occasionCount - pairCount;

/** Occasions eligible to share a day. Capacity must equal the pairable value
 *  exactly — a 2.0 occasion is never pairable however many assessments it holds. */
export const pairableOccasions = (occasions: Occasion[], cfg: PointsConfig = DEFAULT_POINTS_CONFIG) =>
    occasions.filter((o) => o.capacity === cfg.pairableCapacity);

/** Surfaced so the explainer can show whether a target is reachable at all. */

export interface TargetCheck {
    fieldDaysPerMonth: number;
    ceilingPerMonth: number;
    target: number;
    reachable: boolean;
    requiredPerFieldDay: number;
    shortfall: number;
}

/**
 * The target deliberately exceeds a flat-rate month, and always has: today's
 * target is 15 visits against roughly 14 available days. 30 points is that same
 * 15 doubled, because a full day is 2 points. So a shortfall here is the
 * intended stretch, not a mistake — it is closed by multi-report days and by
 * pairing half-day work, which is exactly what the supplement rewards.
 */
export const checkMonthlyTarget = (cfg: PointsConfig = DEFAULT_POINTS_CONFIG): TargetCheck => {
    const fieldDaysPerMonth = cfg.fieldDaysPerYear / 12;
    const maxPerDay = Math.max(...Object.values(cfg.industryRates), cfg.consultancyFullPoints);
    const ceilingPerMonth = fieldDaysPerMonth * maxPerDay;
    return {
        fieldDaysPerMonth,
        ceilingPerMonth,
        target: cfg.monthlyPointsTarget,
        reachable: cfg.monthlyPointsTarget <= ceilingPerMonth,
        requiredPerFieldDay: cfg.monthlyPointsTarget / fieldDaysPerMonth,
        shortfall: Math.max(0, cfg.monthlyPointsTarget - ceilingPerMonth),
    };
};

/** Field days in a month, and the points a consultant can consume in one. */
export const fieldDaysPerMonth = (cfg: PointsConfig = DEFAULT_POINTS_CONFIG) => cfg.fieldDaysPerYear / 12;

/**
 * Points available in a period — field days x a full day's points. This is the
 * denominator for utilisation: how much of the diary was actually consumed.
 *
 * Use CAPACITY points against this, not recognition. Recognition includes the
 * per-report supplement, which deliberately does not consume the day, so
 * measuring it against available points would let a consultant exceed 100%
 * utilisation by writing extra reports. Recognition against the target is a
 * separate, performance question.
 */
export const availablePoints = (fieldDays: number, cfg: PointsConfig = DEFAULT_POINTS_CONFIG) =>
    Math.round(fieldDays * cfg.consultancyFullPoints * 10) / 10;

/** Formats points for display: 1 -> "1", 1.5 -> "1.5". */
export const fmtPoints = (p: number | null | undefined) =>
    p == null ? "—" : Number.isInteger(p) ? String(p) : p.toFixed(1);
